import { useState } from 'react';
import { useRouter } from 'next/router';

import { Anchor, Box, Heading, List, Paragraph } from '@zillow/constellation-10';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';

import SFCostAndFeesTotalMonthlyPriceWithFeeAlert from '../../../../../srcLegacy/shared/components/Alerts/SFCostAndFeesTotalMonthlyPriceWithFeeAlert';
import SFTotalMonthlyPriceWithFeeAcknowledgementModal from '../../../modals/SFTotalMonthlyPriceWithFeeAcknowledgementModal';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import { emitLearnMoreEvent } from '../../../../analytics/events/singleFamilyTotalMonthlyPrice';
import { useAuth } from '../../../AuthProvider';
import CostsAndFeesFormField from '../../../PostingPath/CostsAndFeesFormField';
import SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert from '../../../SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert';
import PageWizard from '../../../Wizard/PageWizard';
import WizardTipCardContainer from '../../../WizardTipCardContainer';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const CostsAndFees = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { user } = useAuth();
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const isTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false;
  const isOptInTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFee?.isOptInEligible ?? false;
  const displayTotalMonthlyPriceWithFee = listingDetails?.displayTotalMonthlyPriceWithFee ?? false;

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  const [showAcknowledgementModal, setShowAcknowledgementModal] = useState(false);

  const onNextClick = () => {
    if (canAccessTotalMonthlyPriceWithFee && !displayTotalMonthlyPriceWithFee) {
      setShowAcknowledgementModal(true);
      return;
    }

    onGoToNextStep?.();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="CostsAndFees">
        <Box css={wizardFlexContainerStyles}>
          <Box
            data-testid="FormSectionContainer"
            css={wizardFormSectionContainerStyles.raw({ isInMultiplexListingFlow: false })}
          >
            <Heading textStyle="heading-sm" level={2} css={wizardHeadingStyles}>
              What additional costs and fees do you charge?
            </Heading>

            <Paragraph css={wizardParagraphStyles}>
              {isOptInTotalMonthlyPriceWithFeeEligible ? (
                <>
                  These details help renters know the real cost of renting your property, and helps you meet relevant
                  fee transparency laws.{' '}
                </>
              ) : (
                <>
                  We&apos;ve highlighted some of the most common fee{' '}
                  {canAccessTotalMonthlyPriceWithFee ? 'types' : 'categories'}. These details help renters know the real
                  cost of renting your property before they apply.
                </>
              )}
            </Paragraph>

            {canAccessTotalMonthlyPriceWithFee && <SFCostAndFeesTotalMonthlyPriceWithFeeAlert />}
            {isOptInTotalMonthlyPriceWithFeeEligible && (
              <SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert triggerLocation="posting_path" />
            )}

            <CostsAndFeesFormField displayedLocation="posting_path" />
          </Box>

          <WizardTipCardContainer
            header="Tips for adding costs and fees"
            showDesktopViewBreakpoint={showDesktopViewBreakpoint}
          >
            <div data-testid="WizardTipContent">
              <List.Root appearance="bullet">
                {canAccessTotalMonthlyPriceWithFee || isOptInTotalMonthlyPriceWithFeeEligible ? (
                  <>
                    {canAccessTotalMonthlyPriceWithFee && (
                      <>
                        <List.Item>
                          Make sure to add any required monthly fees, and note if any are already included in the base
                          rent.
                        </List.Item>

                        <List.Item>
                          Fee transparency helps renters make informed decisions and improves efficiency for you when
                          placing a renter.{' '}
                          <Anchor
                            target="_blank"
                            href="https://www.zillow.com/rentals-network/pricing-transparency/"
                            rel="noopener noreferrer"
                            onClick={() => {
                              if (listingDetails && user) {
                                emitLearnMoreEvent({
                                  currentProperty: listingDetails,
                                  user,
                                  triggerObject: 'costs_and_fees',
                                  triggerLocation: 'posting_path',
                                });
                              }
                            }}
                          >
                            Learn more
                          </Anchor>{' '}
                          about regulations in your area.
                        </List.Item>
                      </>
                    )}

                    {isOptInTotalMonthlyPriceWithFeeEligible && (
                      <>
                        <List.Item>
                          Required monthly fees can be included in your base rent or added separately here.
                        </List.Item>

                        <List.Item>
                          Once your required monthly fees are accounted for, we suggest opting to show the total monthly
                          price on your listing.
                        </List.Item>
                      </>
                    )}

                    <List.Item>Wait to add application fees — we&apos;ll ask about those last.</List.Item>
                  </>
                ) : (
                  <>
                    <List.Item>Wait to add application fees — we&apos;ll ask about those last.</List.Item>

                    <List.Item>Only add the fees you need, and note which are included in the base rent.</List.Item>

                    <List.Item>
                      Some states require disclosing certain fees — be sure to check what&apos;s required for your area.
                    </List.Item>
                  </>
                )}
              </List.Root>
            </div>
          </WizardTipCardContainer>
        </Box>
      </div>

      {showAcknowledgementModal && (
        <SFTotalMonthlyPriceWithFeeAcknowledgementModal
          onClose={() => setShowAcknowledgementModal(false)}
          onSubmit={() => {
            setShowAcknowledgementModal(false);
            onGoToNextStep?.();
          }}
        />
      )}
    </PageWizard>
  );
};

export default CostsAndFees;
